/**
 * Concept-value APIs used by Plan & Automation (AiPlan).
 * Slimmed from gri_fe regulationsService for aihub-fe.
 */
import { apiClient, type ApiResponse } from "./apiClient";

export interface ConceptValue {
  id: string;
  code: string;
  name: string;
  [key: string]: unknown;
}

export interface ConceptGlobalFilterItem {
  code: string;
  name: string;
  description?: string;
  dimension?: string;
  concept_code?: string;
  concept_api_code?: string;
  concept_api_name?: string;
}

interface ConceptsValuesSearchItem {
  concept: {
    code: string;
    name: string;
    display_name?: string;
    description?: string;
  };
  values: Array<{ code: string; name: string; description?: string | null }>;
}

/** Legacy map kept for search-name resolution when only a display label is known. */
export const CONCEPT_NAME_TO_DIMENSION: Record<string, string> = {
  ProductType: "Product Type",
  IngredientName: "Ingredient",
  TherapeuticArea: "Therapeutic Area",
  Department: "Department",
};

export function resolveConceptNamesForSearch(
  conceptCode: string,
  conceptName?: string,
): string[] {
  for (const value of [conceptCode, conceptName]) {
    const trimmed = value?.trim();
    if (!trimmed) continue;

    for (const [apiName, dimension] of Object.entries(CONCEPT_NAME_TO_DIMENSION)) {
      if (
        dimension.toLowerCase() === trimmed.toLowerCase() ||
        apiName.toLowerCase() === trimmed.toLowerCase()
      ) {
        return [apiName];
      }
    }
  }

  const primary = conceptCode.trim() || conceptName?.trim() || "";
  return primary ? [primary] : [];
}

function mapConceptValuesSearchItems(
  rawData: ConceptsValuesSearchItem[],
): ConceptGlobalFilterItem[] {
  const items: ConceptGlobalFilterItem[] = [];

  for (const item of rawData) {
    const conceptName = item.concept?.name ?? "";
    const conceptDisplayName = item.concept?.display_name?.trim();
    // Prefer API display_name; do not invent labels from machine names.
    const dimension =
      conceptDisplayName ||
      CONCEPT_NAME_TO_DIMENSION[conceptName] ||
      conceptName;
    const conceptApiCode = item.concept?.code;
    const conceptApiName = item.concept?.name;
    for (const v of item.values ?? []) {
      if (!v?.code) continue;
      items.push({
        code: v.code,
        name: v.name ?? v.code,
        description: v.description ?? "",
        dimension,
        concept_code: conceptApiCode,
        concept_api_code: conceptApiCode,
        concept_api_name: conceptApiName,
      });
    }
  }
  return items;
}

function extractUserSearchResponseData(
  payload: unknown,
): ConceptsValuesSearchItem[] {
  if (Array.isArray(payload)) {
    return payload as ConceptsValuesSearchItem[];
  }
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.data)) {
    return record.data as ConceptsValuesSearchItem[];
  }
  if (Array.isArray(record.items)) {
    return record.items as ConceptsValuesSearchItem[];
  }
  if ("concept" in record && "values" in record) {
    return [payload as ConceptsValuesSearchItem];
  }
  return [];
}

const conceptsValuesSearchInflight = new Map<
  string,
  Promise<ApiResponse<ConceptGlobalFilterItem[]>>
>();

function buildConceptsValuesSearchKey(
  conceptNames: string,
  searchName: string | undefined,
  pageSize: number,
  page: number,
): string {
  return `${conceptNames}|${searchName ?? ""}|${pageSize}|${page}`;
}

export async function getConceptsValuesSearch(
  conceptNames: string[] | undefined,
  searchName: string | undefined,
  pageSize: number = 10,
  page: number = 1,
): Promise<ApiResponse<ConceptGlobalFilterItem[]>> {
  const conceptNamesParam = (conceptNames ?? [])
    .map((name) => name.trim())
    .filter(Boolean)
    .join(",");
  if (!conceptNamesParam) {
    return {
      status: "success",
      code: 200,
      message: "Skipped: concept_names required",
      data: [],
    };
  }

  const searchNameTrimmed =
    typeof searchName === "string" ? searchName.trim() : undefined;
  const cacheKey = buildConceptsValuesSearchKey(
    conceptNamesParam,
    searchNameTrimmed,
    pageSize,
    page,
  );
  const existing = conceptsValuesSearchInflight.get(cacheKey);
  if (existing) return existing;

  const params: Record<string, string | number | string[]> = {
    page_size: pageSize,
    page,
    concept_names: conceptNamesParam,
  };
  if (searchNameTrimmed) params.name = searchNameTrimmed;

  const request = (async () => {
    try {
      const result = await apiClient.get<ConceptsValuesSearchItem[]>(
        "/knowledge_manager/concepts/values/user-search",
        params,
      );

      const rawData = extractUserSearchResponseData(result.data ?? result);

      return {
        status: (result.status as "success" | "error") || "success",
        code: (result.code as number) ?? 200,
        message: (result.message as string) || "Success",
        data: mapConceptValuesSearchItems(rawData),
      };
    } catch (error) {
      console.error("Failed to get concepts from values/search:", error);
      return {
        status: "error" as const,
        code: 500,
        message: error instanceof Error ? error.message : "Unknown error",
        data: [],
      };
    }
  })().finally(() => {
    conceptsValuesSearchInflight.delete(cacheKey);
  });

  conceptsValuesSearchInflight.set(cacheKey, request);
  return request;
}

export function resetConceptsValuesSearchCache(): void {
  conceptsValuesSearchInflight.clear();
}

export interface GetConceptValuesOptions {
  page?: number;
  page_size?: number;
  searchBy?: "name" | "code";
}

export async function getConceptValues(
  conceptName: string,
  searchName?: string,
  options?: GetConceptValuesOptions,
): Promise<ApiResponse<ConceptValue[]>> {
  const params: Record<string, string | number> = {
    concept_name: conceptName,
  };

  if (searchName) {
    if (options?.searchBy === "code") {
      params.code = searchName;
    } else {
      params.name = searchName;
    }
  }
  if (options?.page != null) {
    params.page = options.page;
  }
  if (options?.page_size != null) {
    params.page_size = options.page_size;
  }

  try {
    const result = await apiClient.get<
      | ConceptValue[]
      | {
          items?: ConceptValue[];
          values?: ConceptValue[];
          data?: ConceptValue[];
          total?: number;
        }
    >("/knowledge_manager/concepts/values", params);
    let data: ConceptValue[] = [];
    if (Array.isArray(result.data)) {
      data = result.data;
    } else if (result.data && typeof result.data === "object") {
      const wrapped = result.data as {
        items?: ConceptValue[];
        values?: ConceptValue[];
        data?: ConceptValue[];
      };
      if (Array.isArray(wrapped.data)) {
        data = wrapped.data;
      } else if (Array.isArray(wrapped.items)) {
        data = wrapped.items;
      } else if (Array.isArray(wrapped.values)) {
        data = wrapped.values;
      }
    }
    return {
      status: (result.status as "success" | "error") || "success",
      code: result.code,
      message: result.message || "Success",
      data,
    };
  } catch (error) {
    console.error(`Failed to get concept values for ${conceptName}:`, error);
    return {
      status: "error",
      code: 500,
      message: error instanceof Error ? error.message : "Unknown error",
      data: [],
    };
  }
}
